import { MessageBubble, MessageBubbleProps, MessageAttachment } from "@tellescope/ui";
import { useSecureFileUrl } from "@tellescope/tanstack-query";
import { useFilesFromSecureNames, useSecureFileUrls } from "../hooks";
import { useMemo } from "react";

export interface ChatAttachment {
  type: string;
  name?: string;
  secureName: string;
}

export interface MessageBubbleWithSecureAvatarProps extends Omit<MessageBubbleProps, "attachments"> {
  chatAttachments?: ChatAttachment[];
}

/**
 * Extracts the underlying Tellescope file `secureName` from a public file URL of the form
 * `https://<bucket>.s3.amazonaws.com/<env>/<businessId>/<secureName>`. Returns undefined for
 * non-Tellescope URLs (e.g. Twilio MMS media) so they are left untouched.
 */
const extractTellescopeFileSecureName = (url: string): string | undefined => {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("tellescope")) return undefined;
    const segment = parsed.pathname.split("/").filter(Boolean).pop();
    return segment ? decodeURIComponent(segment) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Wrapper component for MessageBubble that resolves secure file references for display.
 *
 * - Avatar identifiers (secure file names or URLs) are resolved to displayable URLs.
 * - Chat attachment secureNames are resolved to download URLs.
 * - Direct media URLs (e.g. SMS `mediaURLs`) are passed through unchanged.
 * - Attachments that arrive without a name (e.g. SMS `mediaURLs`, which reference a Tellescope
 *   file by URL) have their display name and type recovered from the underlying file record.
 *
 * @example
 * ```tsx
 * <MessageBubbleWithSecureAvatar
 *   content="Check out these images!"
 *   timestamp={new Date()}
 *   isOutbound={false}
 *   sender={{ name: "John Doe", avatar: "secure-file-name-or-url" }}
 *   chatAttachments={[{ type: "image", secureName: "abc123", name: "photo.jpg" }]}
 * />
 * ```
 */
export const MessageBubbleWithSecureAvatar = ({
  sender,
  chatAttachments,
  ...rest
}: MessageBubbleWithSecureAvatarProps) => {
  const resolvedAvatarUrl = useSecureFileUrl(sender.avatar);
  const attachmentSources = useSecureFileUrls(chatAttachments?.map(({ secureName }) => secureName));

  // Attachments without a name (e.g. SMS mediaURLs) reference a Tellescope file by URL; resolve
  // those files to recover their display name and type.
  const unnamedFileSecureNames = useMemo(
    () =>
      chatAttachments?.flatMap((attachment) => {
        if (attachment.name) return [];
        const fileSecureName = extractTellescopeFileSecureName(attachment.secureName);
        return fileSecureName ? [fileSecureName] : [];
      }) ?? [],
    [chatAttachments]
  );
  const { files } = useFilesFromSecureNames(unnamedFileSecureNames);
  const filesBySecureName = useMemo(() => new Map(files.map((file) => [file.secureName, file])), [files]);

  const attachmentsData = useMemo(
    () =>
      chatAttachments?.reduce<MessageAttachment[]>((acc, attachment) => {
        const url = attachmentSources.find(({ secureName }) => secureName === attachment.secureName)?.url;
        if (!url) return acc;

        const fileSecureName = attachment.name ? undefined : extractTellescopeFileSecureName(attachment.secureName);
        const resolvedFile = fileSecureName ? filesBySecureName.get(fileSecureName) : undefined;

        acc.push({
          url,
          type: attachment.type || resolvedFile?.type || "",
          name: attachment.name || resolvedFile?.name,
        });
        return acc;
      }, []) ?? [],
    [attachmentSources, chatAttachments, filesBySecureName]
  );
  return (
    <MessageBubble
      {...rest}
      sender={{
        ...sender,
        avatar: resolvedAvatarUrl,
      }}
      attachments={attachmentsData}
    />
  );
};
